// Parts: the owner's phone, in the maker's brand. The unit's assistant explains a fault and offers the part
// that fits that serial; checkout under the maker's name; the installer is told to fit it and the order is
// written back to the ERP. Then a consumable reminder with a one-tap reorder.
import React from 'react';
import { C, Explainer, PhoneFrame, Tap, Tick, rise, type ExplainerProps, type SceneDef, type SceneProps, type Unit } from './engine';
import { PRODUCT_FALLBACK, accentOf, partStoryOf, productOf } from './maker';
import { shortName } from './unit';

const T = {
  q: 250,
  typing: 700,
  answer: 1250,
  source: 1600,
  card: 2150,
  orderTap: 3500,
  sheet: 3750,
  payTap: 5150,
  paying: 5300,
  paid: 5850,
  confirmed: 6350,
  row0: 6550,
  row1: 6900,
  row2: 7250,
  reminder: 9300,
  reorderTap: 10650,
  reordered: 10900,
  row3: 11150,
};

/** Catalogue photo of the unit, or a quiet tile in the maker's colour. Never a drawing. */
function UnitPhoto({ u, size, radius = 8 }: { u: Unit; size: number; radius?: number }) {
  const src = productOf(u);
  const [failed, setFailed] = React.useState(false);
  if (!src)
    return (
      <span className="xp-row" style={{ width: size, height: size, flex: 'none', borderRadius: radius, justifyContent: 'center', background: accentOf(u), color: '#fff', fontSize: size * 0.38, fontWeight: 800 }}>
        {u.brand[0]}
      </span>
    );
  return (
    <img
      src={failed ? PRODUCT_FALLBACK : src}
      onError={() => setFailed(true)}
      alt=""
      width={size}
      height={size}
      style={{ width: size, height: size, flex: 'none', borderRadius: radius, objectFit: 'cover', background: '#EEECE6', display: 'block' }}
    />
  );
}

function BrandBar({ u }: { u: Unit }) {
  const accent = accentOf(u);
  return (
    <div className="xp-row" style={{ justifyContent: 'space-between' }}>
      <span className="xp-row" style={{ gap: 6 }}>
        <span className="xp-row" style={{ width: 16, height: 16, borderRadius: 4, justifyContent: 'center', background: accent, color: '#fff', fontSize: 8.5, fontWeight: 800 }}>
          {u.brand[0]}
        </span>
        <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: accent }}>{u.brand}</span>
      </span>
      <span className="xp-mono" style={{ fontSize: 7, color: C.muted }}>
        {u.serial}
      </span>
    </div>
  );
}

function PartsScene({ t, tone, u }: SceneProps) {
  const at = (ms: number) => t >= ms;
  const dark = tone === 'dark';
  const accent = accentOf(u);
  const s = partStoryOf(u);
  const confirmed = at(T.confirmed);
  const reminder = at(T.reminder);
  const paying = at(T.paying) && !at(T.paid);
  const orderNo = `${u.brand[0]}O-10482`;
  const reorderNo = `${u.brand[0]}O-10517`;
  const role = u.partnerRole;
  const partner = shortName(u.partner, 22);

  const orderRows: { on: boolean; k: string; v: React.ReactNode; tone?: 'ok' }[] = [
    { on: at(T.row0), k: `Order ${orderNo}`, v: `${s.part} · ${s.price}` },
    { on: at(T.row1), k: `Routed to ${role}`, v: partner },
    { on: at(T.row2), k: 'Written back to your ERP', v: 'Order, part and serial', tone: 'ok' },
    { on: at(T.row3), k: `Reorder ${reorderNo}`, v: `${s.consumable} · ${s.consumablePrice}` },
  ];

  return (
    <>

      <PhoneFrame x={34} y={18} w={206} h={392} tone={tone}>
        {/* ---- screen 1: ask → answer → part ---- */}
        <div className="xp-abs" style={{ inset: 0, padding: '33px 12px 0', background: '#FAFAF7', visibility: confirmed ? 'hidden' : 'visible' }}>
          <BrandBar u={u} />
          <div className="xp-row" style={{ gap: 8, marginTop: 9, padding: 6, borderRadius: 10, background: '#fff', boxShadow: `0 0 0 1px ${C.line}` }}>
            <UnitPhoto u={u} size={30} radius={6} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.model}</div>
              <div className="xp-row" style={{ gap: 4, fontSize: 7.5, color: C.emerald, fontWeight: 600, whiteSpace: 'nowrap' }}>
                <span style={{ width: 5, height: 5, borderRadius: 5, background: C.emerald }} />
                Warranty active
              </div>
            </div>
          </div>
          {/* owner question */}
          <div className="xp-a" style={{ ...rise(at(T.q), 0, 6), marginTop: 8, marginLeft: 'auto', maxWidth: '82%', padding: '6px 9px', borderRadius: '12px 12px 3px 12px', background: accent, color: '#fff', fontSize: 9, lineHeight: 1.3 }}>
            {s.q}
          </div>
          {/* typing → answer */}
          <div style={{ position: 'relative', marginTop: 6 }}>
            {at(T.typing) && !at(T.answer) && (
              <div className="xp-row" style={{ gap: 3, padding: '8px 10px', width: 40, borderRadius: 12, background: '#fff', boxShadow: `0 0 0 1px ${C.line}` }}>
                {[0, 1, 2].map((i) => (
                  <span key={i} style={{ width: 4, height: 4, borderRadius: 4, background: C.muted, animation: `xp-blink 1s ${i * 0.15}s infinite` }} />
                ))}
              </div>
            )}
            <div className="xp-a" style={{ ...rise(at(T.answer), 0, 6), display: at(T.answer) ? 'block' : 'none', maxWidth: '94%', padding: '7px 9px', borderRadius: '12px 12px 12px 3px', background: '#fff', boxShadow: `0 0 0 1px ${C.line}`, fontSize: 8.8, lineHeight: 1.35, color: C.ink }}>
              {s.a}
              <div className="xp-a xp-mono" style={{ ...rise(at(T.source), 0, 3), marginTop: 5, fontSize: 6.8, color: accent, letterSpacing: '.02em' }}>
                {s.source}
              </div>
            </div>
          </div>
          {/* inline part card */}
          <div className="xp-a" style={{ ...rise(at(T.card), 0, 10), marginTop: 7, padding: 8, borderRadius: 12, background: '#fff', boxShadow: `0 0 0 1px ${C.line}, 0 10px 20px -14px rgba(24,19,67,.35)` }}>
            <div className="xp-row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <UnitPhoto u={u} size={36} radius={7} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 9.8, fontWeight: 700, lineHeight: 1.15 }}>{s.part}</div>
                <div className="xp-row" style={{ gap: 4, marginTop: 2, fontSize: 7.4, color: C.emerald, fontWeight: 600 }}>
                  <Tick on={at(T.card + 200)} size={9} color={C.emerald} />
                  <span style={{ whiteSpace: 'nowrap' }}>Fits {u.serial}</span>
                </div>
              </div>
            </div>
            <div className="xp-row" style={{ justifyContent: 'space-between', gap: 8, marginTop: 7 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '-.01em' }}>
                  {s.price} <span style={{ fontSize: 6.8, fontWeight: 500, color: C.muted, letterSpacing: 0 }}>illustrative</span>
                </div>
                <div style={{ fontSize: 7.2, color: C.muted, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Fitted by {partner}</div>
              </div>
              <span style={{ flex: 'none', padding: '6px 12px', borderRadius: 20, background: accent, color: '#fff', fontSize: 9, fontWeight: 600, transform: at(T.orderTap) && !at(T.sheet) ? 'scale(.94)' : 'none', transition: 'transform .12s' }}>Order</span>
            </div>
          </div>
          <Tap x={164} y={311} on={at(T.orderTap) && !at(T.sheet + 100)} />

          {/* checkout sheet */}
          <div className="xp-abs" style={{ inset: 0, background: 'rgba(24,19,67,.28)', opacity: at(T.sheet) ? 1 : 0, transition: 'opacity .2s' }} />
          <div
            className="xp-abs"
            style={{
              left: 0,
              right: 0,
              bottom: 0,
              height: 222,
              background: '#fff',
              borderRadius: '16px 16px 0 0',
              padding: '8px 14px',
              transform: at(T.sheet) ? 'translateY(0)' : 'translateY(105%)',
              transition: 'transform .45s var(--xe)',
            }}
          >
            <div style={{ width: 30, height: 3, borderRadius: 3, background: C.line, margin: '0 auto 9px' }} />
            <div className="xp-row" style={{ justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, fontWeight: 700 }}>Checkout</span>
              <span style={{ fontSize: 7.5, color: C.muted }}>Sold by {u.brand}</span>
            </div>
            {[
              [s.part, s.price],
              ['Fitting', `By ${partner}`],
              ['Delivered to', `Your ${role}`],
            ].map(([k, v], i) => (
              <div key={k} className="xp-row" style={{ justifyContent: 'space-between', gap: 8, padding: '6px 0', borderTop: i ? `1px solid ${C.line}` : 'none', marginTop: i ? 0 : 6, fontSize: 8.6 }}>
                <span style={{ color: i ? C.muted : C.ink, fontWeight: i ? 400 : 600 }}>{k}</span>
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 104 }}>{v}</span>
              </div>
            ))}
            <div style={{ position: 'relative', marginTop: 9, height: 32, borderRadius: 9, background: '#111', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 600, transform: at(T.payTap) && !at(T.paying) ? 'scale(.97)' : 'none', transition: 'transform .12s' }}>
              <span style={{ visibility: at(T.paying) ? 'hidden' : 'visible' }}>Pay</span>
              {paying && <span className="xp-abs" style={{ left: '50%', top: '50%', width: 12, height: 12, margin: -6, borderRadius: 12, border: '1.6px solid rgba(255,255,255,.35)', borderTopColor: '#fff', animation: 'xp-spin .7s linear infinite' }} />}
              {at(T.paid) && (
                <span className="xp-abs" style={{ left: '50%', top: '50%', marginLeft: -7, marginTop: -7 }}>
                  <Tick on size={14} />
                </span>
              )}
            </div>
            <div style={{ marginTop: 6, fontSize: 6.8, color: C.muted, textAlign: 'center' }}>Paid to {u.brand}. Your {role} is told straight away.</div>
            <Tap x={96} y={175} on={at(T.payTap) && !at(T.paying + 150)} />
          </div>
        </div>

        {/* ---- screen 2: confirmed (cut) ---- */}
        <div className="xp-abs" style={{ inset: 0, padding: '33px 12px 0', background: '#fff', visibility: confirmed && !reminder ? 'visible' : 'hidden' }}>
          <BrandBar u={u} />
          <div className="xp-a" style={{ ...rise(confirmed, 0, 10), marginTop: 18, width: 40, height: 40, borderRadius: 40, background: accent, display: 'grid', placeItems: 'center' }}>
            <Tick on={at(T.confirmed + 150)} size={21} />
          </div>
          <div className="xp-a" style={{ ...rise(confirmed, 80), marginTop: 10, fontSize: 16, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.1 }}>
            Order confirmed
          </div>
          <div className="xp-a" style={{ ...rise(confirmed, 140), marginTop: 5, fontSize: 9.5, lineHeight: 1.35, color: C.ink }}>
            {partner} notified to fit it.
          </div>
          <div className="xp-a" style={{ ...rise(confirmed, 220), marginTop: 12, borderRadius: 10, boxShadow: `inset 0 0 0 1px ${C.line}` }}>
            {[
              ['Order', orderNo],
              ['Part', s.part],
              ['For', `${u.model} · ${u.serial}`],
            ].map(([k, v], i) => (
              <div key={k} style={{ padding: '6px 10px', borderTop: i ? `1px solid ${C.line}` : 'none' }}>
                <div className="xp-cap" style={{ fontSize: 7, color: C.muted }}>
                  {k}
                </div>
                <div className={i === 0 ? 'xp-mono' : ''} style={{ fontSize: i === 0 ? 8.5 : 9.5, fontWeight: 600, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ---- screen 3: consumable reminder (cut) ---- */}
        <div className="xp-abs" style={{ inset: 0, padding: '33px 12px 0', background: '#FAFAF7', visibility: reminder ? 'visible' : 'hidden' }}>
          <BrandBar u={u} />
          <div style={{ marginTop: 12, fontSize: 15, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.1 }}>
            Your {u.model.replace(`${u.brand} `, '')}
          </div>
          <div style={{ fontSize: 8, color: C.muted, marginTop: 2 }}>{u.ownerShort}</div>
          <div className="xp-a" style={{ ...rise(at(T.reminder + 150), 0, 10), marginTop: 12, padding: 10, borderRadius: 12, background: '#fff', boxShadow: `0 0 0 1px ${C.line}, 0 10px 20px -14px rgba(24,19,67,.35)` }}>
            <div className="xp-row" style={{ gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: 6, background: '#D4A045' }} />
              <span className="xp-cap" style={{ fontSize: 7, color: '#7A5410' }}>
                {s.dueTitle}
              </span>
            </div>
            <div style={{ fontSize: 9, lineHeight: 1.35, marginTop: 5, color: C.ink }}>{s.dueLine}</div>
            <div className="xp-row" style={{ gap: 8, marginTop: 8, padding: '7px 0 0', borderTop: `1px solid ${C.line}` }}>
              <UnitPhoto u={u} size={28} radius={6} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.consumable}</div>
                <div style={{ fontSize: 7.2, color: C.muted }}>
                  {s.consumablePrice} · fits your {u.model.replace(`${u.brand} `, '')}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 9,
                height: 28,
                borderRadius: 28,
                display: 'grid',
                placeItems: 'center',
                fontSize: 9.5,
                fontWeight: 600,
                background: at(T.reordered) ? 'rgba(63,174,108,.14)' : accent,
                color: at(T.reordered) ? C.emerald : '#fff',
                transform: at(T.reorderTap) && !at(T.reordered) ? 'scale(.97)' : 'none',
                transition: 'transform .12s, background .2s, color .2s',
              }}
            >
              {at(T.reordered) ? 'Reordered · same address' : 'Reorder in one tap'}
            </div>
            <Tap x={86} y={136} on={at(T.reorderTap) && !at(T.reordered + 200)} />
          </div>
        </div>
      </PhoneFrame>

      {/* ---- your side: the unit and the orders ---- */}
      <div className="xp-abs xp-cap" style={{ left: 266, top: 34, fontSize: 8.5, color: 'var(--xp-muted)' }}>
        What the assistant knows
      </div>
      <div className="xp-abs xp-card" style={{ left: 266, top: 52, width: 196, padding: '9px 12px' }}>
        <div className="xp-row" style={{ gap: 8 }}>
          <UnitPhoto u={u} size={26} radius={5} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.title}</div>
            <div className="xp-mono" style={{ fontSize: 7.5, color: C.muted }}>
              {u.serial}
            </div>
          </div>
        </div>
        <div className="xp-row" style={{ gap: 5, marginTop: 7, flexWrap: 'wrap' }}>
          {[u.Role + ': ' + shortName(u.partner, 18), 'Warranty active'].map((x) => (
            <span key={x} style={{ fontSize: 7.2, padding: '2px 6px', borderRadius: 10, background: C.alt, color: C.ink, whiteSpace: 'nowrap' }}>
              {x}
            </span>
          ))}
        </div>
      </div>

      <svg className="xp-abs" style={{ left: 0, top: 0 }} width={480} height={360}>
        <path d="M244 230 C 254 230, 254 186, 266 186" fill="none" stroke={dark ? C.lime : C.emerald} strokeWidth="1.25" strokeDasharray="70" strokeDashoffset={at(T.row0) ? 0 : 70} style={{ transition: 'stroke-dashoffset .4s var(--xe)' }} />
      </svg>

      <div className="xp-abs xp-cap" style={{ left: 266, top: 152, fontSize: 8.5, color: 'var(--xp-muted)', visibility: at(T.row0) ? 'visible' : 'hidden' }}>
        Your orders
      </div>
      <div className="xp-abs xp-card" style={{ left: 266, top: 170, width: 196, overflow: 'hidden', visibility: at(T.row0) ? 'visible' : 'hidden' }}>
        {orderRows.filter((r) => r.on).map((r, i) => (
          <div
            key={i}
            className="xp-a"
            style={{
              ...rise(r.on, 0, 6),
              padding: '7px 12px',
              borderTop: i ? `1px solid ${C.line}` : 'none',
              marginTop: i === 3 ? 0 : 0,
              background: i === 3 ? 'rgba(63,174,108,.06)' : 'transparent',
            }}
          >
            <div className="xp-row" style={{ justifyContent: 'space-between' }}>
              <span className="xp-cap" style={{ fontSize: 7, color: C.muted, letterSpacing: '.08em' }}>
                {r.k}
              </span>
              {r.tone === 'ok' && (
                <span className="xp-row" style={{ width: 11, height: 11, borderRadius: 11, justifyContent: 'center', background: C.emerald }}>
                  <Tick on={r.on} size={8} />
                </span>
              )}
            </div>
            <div style={{ fontSize: 9.3, fontWeight: 600, marginTop: 1, color: r.tone === 'ok' ? C.emerald : C.indigo, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.v}</div>
          </div>
        ))}
      </div>
      <style>{'@keyframes xp-spin{to{transform:rotate(360deg)}}'}</style>
    </>
  );
}

export const partsDef: SceneDef = {
  name: 'Parts',
  label: (u) => {
    const s = partStoryOf(u);
    return `On the owner's phone, in the ${u.brand} brand: the assistant explains a fault on the ${u.model} and offers the ${s.part.toLowerCase()} that fits serial ${u.serial}. The owner pays, ${u.partner} is told to fit it, and the order is routed to the ${u.partnerRole} and written back to the ERP. Later a reminder offers the ${s.consumable.toLowerCase()} as a one-tap reorder.`;
  },
  W: 480,
  H: 360,
  duration: 13500,
  end: 12800,
  steps: [
    { at: 0, label: 'The unit explains the fault' },
    { at: T.card, label: 'The part that fits' },
    { at: T.sheet, label: 'Checkout under your brand' },
    { at: T.confirmed, label: 'Routed and written back' },
    { at: T.reminder, label: 'Consumables, one tap' },
  ],
  Scene: PartsScene,
};

export const PartsExplainer = (p: ExplainerProps) => <Explainer def={partsDef} {...p} />;
